// R11P GEOLOGICAL P0.5-P9A — Portal API error + data-state foundation.
//
// One place that turns a geo-api response into (a) a render-safe, structured
// error that preserves the nested envelope { error: { code, message, requestId } }
// and the HTTP status, and (b) a single data-state the UI renders consistently.
//
// Hard invariant: UNAVAILABLE ≠ ZERO. A failed/empty/restricted read must never
// surface as a measured numeric zero. Use displayCount() for any count a user
// could read as an operational total.

/** The distinct outcomes a Portal surface must be able to show apart. */
export type PortalDataState =
  | 'loading'
  | 'ready'
  | 'empty' // a SUCCESSFUL read that returned no rows (a real, measured zero)
  | 'auth_required' // 401
  | 'restricted' // 403
  | 'not_found' // 404
  | 'conflict' // 409
  | 'validation' // 400 / 422
  | 'unavailable' // 501 / 503 / network / transport
  | 'error'; // 500 and other non-ok

export type PortalErrorKind = Exclude<PortalDataState, 'loading' | 'ready' | 'empty'>;

export interface PortalError {
  kind: PortalErrorKind;
  /** Backend error code where present (e.g. GEOLOGY_SELF_APPROVAL_FORBIDDEN). */
  code: string | null;
  /** Safe, human-readable message. Never a raw response body. */
  message: string;
  /** Correlation id for support/diagnostics, when the backend supplies one. */
  requestId: string | null;
  /** HTTP status (0 for transport/network failure). */
  status: number;
}

/** Map an HTTP status (or 0) to the Portal error kind. */
export function kindFromStatus(status: number): PortalErrorKind {
  if (status === 401) return 'auth_required';
  if (status === 403) return 'restricted';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 400 || status === 422) return 'validation';
  if (status === 0 || status === 501 || status === 503) return 'unavailable';
  return 'error';
}

interface RawEnvelope {
  error?: unknown;
  message?: unknown;
  code?: unknown;
  requestId?: unknown;
}

/**
 * Normalise a geo-api error body + status into a structured PortalError.
 * Supports: { error: "msg" } | { message } | { error: { code, message, requestId } }
 * and transport failures (status 0). Never returns a raw object to the UI.
 */
export function normalizeApiError(body: unknown, status: number): PortalError {
  const kind = kindFromStatus(status);
  let code: string | null = null;
  let requestId: string | null = null;
  let message = '';

  if (body && typeof body === 'object') {
    const p = body as RawEnvelope;
    if (typeof p.error === 'string' && p.error.trim()) {
      message = p.error.trim();
    } else if (p.error && typeof p.error === 'object') {
      const nested = p.error as RawEnvelope;
      if (typeof nested.code === 'string') code = nested.code;
      if (typeof nested.requestId === 'string') requestId = nested.requestId;
      if (typeof nested.message === 'string' && nested.message.trim()) message = nested.message.trim();
    }
    if (!message && typeof p.message === 'string' && p.message.trim()) message = p.message.trim();
    if (!code && typeof p.code === 'string') code = p.code;
    if (!requestId && typeof p.requestId === 'string') requestId = p.requestId;
  }

  if (!message) {
    message = status === 0
      ? 'The service could not be reached.'
      : `Request failed (${status}).`;
  }
  return { kind, code, message, requestId, status };
}

/** Back-compat: a render-safe string (callers that only need a message). */
export function errorMessage(body: unknown, status: number): string {
  return normalizeApiError(body, status).message;
}

/**
 * The single data-state for a settled API result. `isEmpty` lets a caller mark a
 * successful-but-no-rows read as the honest `empty` state. A non-ok result is
 * mapped by its status so 401/403/404/409/validation/unavailable stay distinct —
 * never collapsed together, and never shown as a zero.
 */
export function stateFromResult(
  result: { status: number; error: unknown | null; data?: unknown },
  opts?: { isEmpty?: (data: unknown) => boolean }
): PortalDataState {
  if (result.error || result.status === 0 || result.status >= 400) {
    return kindFromStatus(result.status || 0);
  }
  if (opts?.isEmpty && opts.isEmpty(result.data)) return 'empty';
  if (Array.isArray(result.data) && result.data.length === 0) return 'empty';
  return 'ready';
}

/**
 * UNAVAILABLE ≠ ZERO. Returns the count ONLY for a successful read; any failure,
 * loading or restricted state returns null so the UI shows a state, never a 0.
 */
export function displayCount(ok: boolean, count: number | null | undefined): number | null {
  if (!ok) return null;
  return typeof count === 'number' && Number.isFinite(count) ? count : null;
}
